import React, { useRef, useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import * as d3 from 'd3';

import { chartStore } from '../../../store/chartStore';

const WIDTH = 690;
const HEIGHT = 450;

const margin = 20;
const padding = 12;
const yAxisWidth = 30;
const xAxisHeight = 18;
const currentRoundHeight = 40 + padding * 2;

const calcRoundTime = (launchTime) => {
  return launchTime ? (Date.now() - launchTime) / 1000 : 0;
};

const calcOdd = (launchTime) => {
  const exponent = calcRoundTime(launchTime) / 8;
  return Math.max(1, 2 ** exponent.toFixed(2));
};

const GameChart = observer(() => {
  const { launchTime, isCrashed, isAnimationState } = chartStore;

  const [xGroup, setXGroup] = useState(null);
  const [yGroup, setYGroup] = useState(null);
  const [bars, setBars] = useState(null);
  const [interval, setInterval] = useState(null);

  const ref = useRef(null);

  const createSvg = () => {
    const svg = d3.select(ref.current).append('svg');

    svg.attr('preserveAspectRatio', 'xMinYMin meet').attr('viewBox', `0 0 ${WIDTH} ${HEIGHT}`);

    setXGroup(svg.append('g').attr('class', 'column-axis'));
    setYGroup(svg.append('g').attr('class', 'odd-axis'));
    setBars(svg.append('g').attr('class', 'pillars'));
  };

  const handleAxisAnimations = () => {
    const y = d3.scaleLinear();
    const yAxis = d3.axisRight(y).ticks(5);

    const x = d3.scaleLinear();
    const xAxis = d3.axisBottom(x).ticks(6);

    const odd = Number(calcOdd(launchTime));
    const time = calcRoundTime(launchTime);

    y.domain([1, odd <= 1.8 ? 1.8 : odd]).range([
      0,
      -HEIGHT + currentRoundHeight + xAxisHeight + margin + 50,
    ]);

    x.domain([1, time <= 6 ? 6 : time]).range([0, WIDTH - yAxisWidth - padding * 4]);

    yGroup &&
      yGroup
        .attr('transform', `translate(${padding}, ${HEIGHT - margin - xAxisHeight - 26})`)
        .attr('font-size', 13)
        .attr('letter-spacing', 0.88)
        .attr('font-family', `'Lato', sans-serif`)
        .transition()
        .ease(d3.easeLinear)
        .duration(100)
        .call(yAxis.tickSize(0).tickFormat((d) => (d === 0 ? '' : `${d.toFixed(1)}x`)));

    xGroup &&
      xGroup
        .attr(
          'transform',
          `translate(${yAxisWidth + padding * 3}, ${HEIGHT - xAxisHeight - margin})`
        )
        .attr('font-size', 13)
        .attr('letter-spacing', 0.88)
        .attr('font-family', `'Lato', sans-serif`)
        .transition()
        .ease(d3.easeLinear)
        .duration(500)
        .call(xAxis.tickSize(0));

    bars &&
      bars
        .attr(
          'transform',
          `translate(${yAxisWidth + padding * 3}, ${HEIGHT - xAxisHeight - margin * 2})`
        )
        .transition()
        .ease(d3.easeLinear)
        .duration(500)
        .call(xAxis.tickSize(-(HEIGHT - 150), 0, 0).tickFormat(''));
  };

  useEffect(() => {
    if (ref.current) {
      d3.select(ref.current).select('svg').remove();
      createSvg();
    }
  }, [ref]);

  useEffect(() => {
    if (xGroup && yGroup) {
      handleAxisAnimations();
      if (isAnimationState) {
        setInterval(d3.interval(handleAxisAnimations, 100));
      }
    }
  }, [xGroup, yGroup, isAnimationState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isCrashed && interval) {
      interval.stop();
    }

    return () => {
      if (interval) {
        interval.stop();
      }
    };
  }, [isCrashed, interval]);

  useEffect(() => {
    if (!isAnimationState) {
      d3.select(ref.current).select('svg').remove();
      createSvg();
      handleAxisAnimations();
    }
  }, [ref, isAnimationState]);

  return <div ref={ref} className='chart-crush' />;
});

export default GameChart;
